// The DocumentStore operations beyond the app's editing (`ProjectEditor`), written as the Swift writes them, for the MCP
// tools and anything else that works on a whole project: ProjectDocument.create, createFragment/createStableFragment,
// putField/ensureStoryFields, createArrangement, forkArrangement (Alternates.swift), `move(_:from:to:parent:index:)`
// between arrangements (and out of the Trash), putEntity (StoryModel.swift), ensureCharacterStudies (CharacterStudy.swift),
// inboxFolder/addDraft (Drafts.swift), mapsFolder/newMap/editMap (DocumentStore+Maps.swift; the map's own writes are
// maps/mapDocument.ts), addAgentComments (ProjectEditing.swift), and FragmentDocument's margin writes
// (addComment/addSynopsisComment/addSuggestion/addTagProposal) and field setters, which the Swift commits with a message
// and without touching `modifiedAt`.
//
// Like `DocumentEditor`, each method is one Automerge change per document it touches, with the Swift's commit message.
import * as A from '@automerge/automerge';
import type { MetadataValue, ArrangementRole, EntityKind, FieldType, FragmentKind, Placement, ProjectType } from '../contract.ts';
import { encodeHeads } from '../automerge/heads.ts';
import { between } from '../automerge/fractionalIndex.ts';
import { scalarLength, sliceScalars } from '../automerge/offsets.ts';
import { ROOT, SwiftReader } from '../automerge/swiftDoc.ts';
import { nextEntityColor } from '../colors.ts';
import { AgentBrief, isAgentBrief } from '../agentBrief.ts';
import { stableUUID } from '../hashing.ts';
import { ancestors, flattened, resolvedParent } from '../model/arrangement.ts';
import { readFragment, readProject, type Arrangement, type ArrangementNode, type MetadataField, type ProjectRecord, type StoryEntity } from '../model/documents.ts';
import { canRemoveField, canRenameField, FIELD_NAME_LIMIT, fieldID, fieldKey, fieldNamed, normalizedFieldName, optionColor, optionID, removalBlocked, renameBlocked, visibleFields, type StarterFieldSet } from '../plan/fields.ts';
import { decodeGridView, encodeGridView, GRID_VIEW_NAME_LIMIT, GRID_VIEW_PREFIX, gridViewID, gridViewKey, gridViewNamed, isBuiltInView, readGridViews, type GridView } from '../plan/gridViews.ts';
import {
  isMusicalOf, isScriptKind, isStageFormat, MADE_MANUSCRIPT_NAMES, projectKind, scriptFormatName, scriptFormatOf, scriptManuscriptName, type ScriptFormat,
} from '../scriptFormat.ts';
import { BookMatter } from '../bookMatter.ts';
import { putJSON } from '../settingsJSON.ts';
import { historySummary, recordImportedHistory, type HistorySummary, type ImportedDay } from '../importedHistory.ts';
import { graphemes, trimCFWhitespaceAndNewlines } from '../unicode.ts';
import { swiftCompare } from '../model/order.ts';
import { addingTag, fragmentTags, removingTag, tagCounts, TAGS_FIELD, type TagCount } from '../tags.ts';
import { mapNote, Maps, type MapContent } from '../maps/map.ts';
import { MapDocument, writeMapKeys } from '../maps/mapDocument.ts';
import type { MapScenesItem } from '../maps/scenes.ts';
import { childrenOf, DocumentEditor, fragmentDocName, isWithinSubtree, makeID, oneLineTitle, putNode, readMetadata, subtree, type EditOptions } from './editProject.ts';
import { ModelError } from './modelError.ts';
import { bool, changeTime, f64, int, str, timestamp, Writer, type Scalar } from './typed.ts';

export { ModelError };

/** `DocumentStore.MapScenesDestination`: where Turn Into Scenes puts the new documents in the manuscript — at the end of
 *  a folder (a node in the manuscript arrangement), at the top level, or in a new folder at the end of the manuscript. */
export type MapScenesDestination = { folder: string } | { topLevel: true } | { newFolder: string };

/** `MetadataField` as `putField` takes it. */
export interface FieldSpec { id: string; name: string; type: FieldType; options?: { id: string; name: string; color?: string }[]; order: number }

/** `AlternateSet` (Alternates.swift), as stored: JSON per set in `settings.alternates`. */
export interface AlternateSet {
  id: string;
  kind: 'fragment' | 'folder' | 'arrangement';
  title: string;
  members: { id: string; name: string; createdAt: Date; fragmentID?: string; arrangementID?: string; rootNodeID?: string }[];
  currentMemberID: string;
  arrangementID?: string;
  nodeID?: string;
  role?: ArrangementRole;
}

/** `StudyKind` (StudyPages.swift): study pages for characters and for places, each kind with its field, folder and ids.
 *  A character's are what the app has always made (`CharacterStudy`); a place's hash its own field (`place-study`), so a
 *  build from before place pages reads one as an ordinary note and never as a character's. */
export type StudyKind = 'character' | 'place';

export interface StudyKindInfo {
  kind: StudyKind;
  entityKind: EntityKind;
  field: string;
  folderTitle: string;
  folderSynopsis: string;
  folderNames: Set<string>;
  prompts: readonly string[];
  readonly template: string;
  guidance(name: string): string;
  settingsKey: string;
  stableID(projectID: string, purpose: string): string;
  fragmentID(projectID: string, entity: string): string;
}

export const STUDY_FOLDER_MARKER = 'folder';

export const STUDY_KINDS: Record<StudyKind, StudyKindInfo> = {
  character: {
    kind: 'character', entityKind: 'character', field: 'character-study', folderTitle: 'Characters',
    folderSynopsis: 'A study page for each character. New characters get one automatically.',
    folderNames: new Set(['characters', 'character', 'cast', 'characterstudies', 'charactersketches', 'people']),
    prompts: ['Role in the story', 'Appearance', 'Personality', 'Voice and speech', 'Background', 'Wants', 'Needs', 'Fears and flaws', 'Arc', 'Relationships', 'Details to remember'],
    get template(): string { return this.prompts.map((p) => `**${p}:** `).join('\n\n'); },
    guidance: (name: string) => `Everything about ${name} in one place. Write after each label, add your own, and put photos above the text.`,
    settingsKey: 'characterStudies',
    stableID: (projectID: string, purpose: string) => stableUUID(`${projectID}|character-study|${purpose}`),
    fragmentID: (projectID: string, entity: string) => stableUUID(`${projectID}|character-study|fragment|${entity}`),
  },
  place: {
    kind: 'place', entityKind: 'place', field: 'place-study', folderTitle: 'Places',
    folderSynopsis: 'A study page for each place. New places get one automatically.',
    folderNames: new Set(['places', 'place', 'settings', 'setting', 'locations', 'location', 'world', 'worldbuilding']),
    prompts: ['Where it is', 'What it looks like', 'Sounds, smells, weather', 'Who is found there', 'Its history', 'What happens here', 'Period details', 'Details to remember'],
    get template(): string { return this.prompts.map((p) => `**${p}:** `).join('\n\n'); },
    guidance: (name: string) => `Everything about ${name} in one place. Write after each label, add your own, and put photos above the text.`,
    settingsKey: 'placeStudies',
    stableID: (projectID: string, purpose: string) => stableUUID(`${projectID}|place-study|${purpose}`),
    fragmentID: (projectID: string, entity: string) => stableUUID(`${projectID}|place-study|fragment|${entity}`),
  },
};

/** `StudyKind.of(_:)`: objects and motifs keep their notes field. */
export const studyKindOf = (kind: EntityKind): StudyKind | null => (kind === 'character' || kind === 'place' ? kind : null);

/** `StudyPages.page(in:)`: the study page a document is, by its metadata (never the folder). */
export function studyPageOf(metadata: Record<string, MetadataValue>): { kind: StudyKind; entityID: string } | null {
  for (const kind of ['character', 'place'] as const) {
    const v = metadata[STUDY_KINDS[kind].field];
    if (v?.type === 'text' && v.value !== STUDY_FOLDER_MARKER) return { kind, entityID: v.value };
  }
  return null;
}

/** `CharacterStudy`. */
export const CharacterStudy = {
  field: 'character-study',
  folderMarker: 'folder',
  folderTitle: 'Characters',
  prompts: [
    'Role in the story', 'Appearance', 'Personality', 'Voice and speech', 'Background',
    'Wants', 'Needs', 'Fears and flaws', 'Arc', 'Relationships', 'Details to remember',
  ] as readonly string[],
  /** A new page's text: each prompt as a bold label to write after. */
  get template(): string { return this.prompts.map((p) => `**${p}:** `).join('\n\n'); },
  guidance: (name: string) => `Everything about ${name} in one place. Write after each label, add your own, and put photos above the text.`,
  stableID: (projectID: string, purpose: string) => stableUUID(`${projectID}|character-study|${purpose}`),
  fragmentID: (projectID: string, character: string) => stableUUID(`${projectID}|character-study|fragment|${character}`),
  /** Folder names a writer (or a Scrivener template) may already use for character sketches. */
  folderNames: new Set(['characters', 'character', 'cast', 'characterstudies', 'charactersketches', 'people']),
};

/** `BookMatter.stableID`. */
export const bookMatterStableID = (projectID: string, purpose: string) => stableUUID(`${projectID}|book-matter|${purpose}`);
/** `BookMatter.field`: the metadata marker on book-matter documents. */
export const BOOK_MATTER_FIELD = 'book-matter';

/** `DocumentStore.bookMatterVersion`: 3, the folders in their own Publishing arrangement. */
export const BOOK_MATTER_VERSION = 3;
/** `DocumentStore.inboxTitle`. */
export const INBOX_TITLE = 'Inbox';

/** `ProjectType.manuscriptKind`. */
export const manuscriptKind = (t: ProjectType): FragmentKind => (t === 'screenplay' ? 'screenplay' : 'prose');

/** `JSONEncoder.suite`: sorted keys, ISO 8601 dates without fractions, nil optionals left out, "/" escaped. */
export function suiteJSON(value: unknown): string {
  const plain = (v: unknown): unknown => {
    if (v instanceof Date) return v.toISOString().replace(/\.\d{3}Z$/, 'Z');
    if (Array.isArray(v)) return v.map(plain);
    if (v && typeof v === 'object') {
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(v).sort(swiftCompare)) {
        const x = (v as Record<string, unknown>)[k];
        if (x !== undefined && x !== null) out[k] = plain(x);
      }
      return out;
    }
    return v;
  };
  return JSON.stringify(plain(value)).replaceAll('/', '\\/');
}

/** `alternateSets()`: every readable set (unreadable JSON skipped, as `try?` does). */
export function alternateSets(project: ProjectRecord): AlternateSet[] {
  const raw = project.settings.alternates;
  if (!raw || typeof raw !== 'object') return [];
  const out: AlternateSet[] = [];
  for (const json of Object.values(raw as Record<string, unknown>)) {
    if (typeof json !== 'string') continue;
    try {
      const s = JSON.parse(json) as AlternateSet;
      if (!s || typeof s.id !== 'string' || !Array.isArray(s.members)) continue;
      s.members = s.members.map((m) => ({ ...m, createdAt: new Date(m.createdAt as unknown as string) }));
      out.push(s);
    } catch {
      continue;
    }
  }
  return out.sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true }));
}

/** `Arrangement.parent(of:)`: the parent node as `tree()` places it. */
export function parentOf(a: Arrangement, nodeID: string): ArrangementNode | undefined {
  const node = a.nodes.get(nodeID);
  const p = node ? resolvedParent(a, node) : undefined;
  return p === undefined ? undefined : a.nodes.get(p);
}

/** A comment as the margin writes return it. */
export interface WrittenComment { id: string; quote: string; anchorStart: number; anchorEnd: number; anchorHeads: string }

/** `AgentComment`: a comment an app-side check (or agent) posts, with a stable id so posting it again adds nothing. */
export interface AgentComment { id: string; fragmentID: string; range: [number, number]; body: string; /** A comment category of the project's, by id. */ category?: string | null }

const letterKey = (s: string) => [...s.toLowerCase()].filter((c) => /\p{L}/u.test(c)).join('');

export class StoreEditor extends DocumentEditor {
  constructor(docs: Map<string, A.Doc<unknown>>, context: EditOptions) {
    super(docs, context);
  }

  // MARK: Creating documents

  /** `ProjectDocument.create`: a new project document ("Create project"), with the standard fields and its manuscript. */
  static createProject(o: { id?: string; title: string; type?: ProjectType; scriptFormat?: ScriptFormat; musical?: boolean; createdAt?: Date;
    locale?: string; actor?: string; arrangementID?: string }): A.Doc<unknown> {
    const type = o.type ?? 'manuscript';
    const scriptFormat = type === 'screenplay' ? o.scriptFormat ?? 'screen' : 'screen';
    // Only a play is a musical; the setting is stored only while on.
    const musical = (o.musical ?? false) && isStageFormat(scriptFormat);
    const createdAt = o.createdAt ?? new Date();
    const id = o.id ?? makeID();
    return A.change(A.init<Record<string, any>>(o.actor ? { actor: o.actor } : undefined), { message: 'Create project', time: changeTime(createdAt) }, (d) => {
      const w = new Writer(d, new SwiftReader(A.init()));
      w.put([], 'schemaVersion', int(1));
      w.put([], 'type', str('project'));
      w.put([], 'id', str(id));
      w.put([], 'epoch', int(0));
      w.put([], 'title', str(o.title));
      w.put([], 'createdAt', timestamp(createdAt));
      d.settings = {};
      w.put(['settings'], 'locale', str(o.locale ?? 'en-AU'));
      w.put(['settings'], 'projectType', str(type));
      if (scriptFormat !== 'screen') w.put(['settings'], 'scriptFormat', str(scriptFormat));
      if (musical) w.put(['settings'], 'musical', bool(true));
      for (const key of ['fragments', 'metadataSchema', 'arrangements', 'entities', 'checkpoints']) d[key] = {};
      const fields: FieldSpec[] = [
        {
          id: 'status', name: 'Status', type: 'enum', order: 0, options: type === 'journal'
            ? [{ id: 'status-idea', name: 'Idea' }, { id: 'status-draft', name: 'Draft' }, { id: 'status-scheduled', name: 'Scheduled' }, { id: 'status-published', name: 'Published' }]
            : [{ id: 'status-todo', name: 'To Do' }, { id: 'status-first-draft', name: 'First Draft' }, { id: 'status-revised', name: 'Revised Draft' },
              { id: 'status-final', name: 'Final Draft' }, { id: 'status-done', name: 'Done' }],
        },
        {
          id: 'label', name: 'Label', type: 'enum', order: 1, options: [
            { id: 'label-red', name: 'Red', color: '0.96 0.55 0.55' }, { id: 'label-orange', name: 'Orange', color: '0.98 0.72 0.45' },
            { id: 'label-yellow', name: 'Yellow', color: '0.98 0.88 0.45' }, { id: 'label-green', name: 'Green', color: '0.55 0.85 0.55' },
            { id: 'label-blue', name: 'Blue', color: '0.5 0.7 0.98' }, { id: 'label-purple', name: 'Purple', color: '0.78 0.6 0.95' },
          ],
        },
        { id: 'pov', name: 'POV', type: 'entity-ref', order: 2 },
        { id: 'word-target', name: 'Word target', type: 'number', order: 3 },
      ];
      if (type === 'manuscript') {
        fields.push({ id: 'section-type', name: 'Section type', type: 'enum', order: fields.length,
          options: ['Part', 'Chapter', 'Scene'].map((n) => ({ id: `section-${n.toLowerCase()}`, name: n })) });
      }
      if (type === 'journal') {
        const order = fields.length;
        fields.push({ id: 'post-number', name: 'Post number', type: 'number', order }, { id: 'publish-date', name: 'Publish date', type: 'date', order: order + 1 },
          { id: 'tags', name: 'Tags', type: 'text', order: order + 2 });
      }
      for (const f of fields) writeField(w, d, f);
      const arrangement = o.arrangementID ?? makeID();
      const path = ['arrangements', arrangement];
      w.put(path, 'name', str(type === 'screenplay' ? scriptManuscriptName(scriptFormat, musical) : type === 'journal' ? 'Posts' : 'Manuscript'));
      w.put(path, 'role', str('manuscript'));
      w.put(path, 'order', f64(0));
      w.ensure([...path, 'nodes']);
    }) as A.Doc<unknown>;
  }

  /** `createFragment`: a fragment document registered in the project but placed nowhere. Returns its id. */
  createFragment(o: { id?: string; kind?: FragmentKind; title: string; createdAt?: Date; text?: string }): string {
    const project = this.project();
    const id = o.id ?? this.id();
    const createdAt = o.createdAt ?? this.now();
    const kind = o.kind ?? 'prose';
    const actor = this.context.newActor?.() ?? this.context.actor;
    const doc = A.change(A.init<Record<string, any>>(actor ? { actor } : undefined), { time: changeTime(createdAt) }, (d) => {
      const w = new Writer(d, new SwiftReader(A.init()));
      writeFragmentKeys(w, d, { id, projectID: project.id, kind, title: o.title, createdAt });
      if (o.text) A.updateText(d as A.Doc<unknown>, ['text'], o.text);
    });
    this.docs.set(fragmentDocName(id), doc as A.Doc<unknown>);
    this.written.add(doc);
    this.change('project', undefined, createdAt, (w) => registerFragment(w, { id, kind, trashed: false, createdAt }));
    return id;
  }

  /** `createStableFragment`: a document built the same on every device (actor and content from its id), so two that
   *  add it before syncing merge; then `modifiedAt` as this device, and registered. */
  createStableFragment(o: { id: string; kind: FragmentKind; title: string; message: string; date: Date },
    fill: (w: Writer, d: Record<string, any>) => void, register: ((write: (w: Writer) => void) => void) | null = null): void {
    const project = this.project();
    const createdAt = project.createdAt;
    const build = (seed: string) => {
      const actor = stableUUID(seed).replaceAll('-', '');
      return A.change(A.init<Record<string, any>>({ actor }), { message: o.message, time: changeTime(createdAt) }, (d) => {
        const w = new Writer(d, new SwiftReader(A.init()));
        writeFragmentKeys(w, d, { id: o.id, projectID: project.id, kind: o.kind, title: o.title, createdAt });
        fill(w, d);
      });
    };
    const draft = build(`stable-document|${o.id}`);
    // From the draft's change hash, as the Swift does: changes encode identically across Automerge versions, saved
    // documents don't (automerge-js 3.5 writes a different document chunk than automerge-swift's 0.7.2).
    const stable = build(`stable-document|${o.id}|` + encodeHeads(A.getHeads(draft)));
    const actor = this.context.newActor?.() ?? this.context.actor;
    const mine = A.clone(stable, actor ? { actor } : undefined);
    const name = fragmentDocName(o.id);
    this.docs.set(name, mine as A.Doc<unknown>);
    this.written.add(mine);
    this.change(name, undefined, o.date, (w) => w.putIfChanged([], 'modifiedAt', timestamp(o.date)));
    const write = (w: Writer) => registerFragment(w, { id: o.id, kind: o.kind, trashed: false, createdAt: o.date });
    // Registered in the caller's project change when it gives one (as the Swift's pending project ops are), else now.
    if (register) register(write);
    else this.change('project', undefined, o.date, write);
  }

  // MARK: The project document

  /** `putField` (+ commit with `message`, or none). */
  putField(field: FieldSpec, message?: string): string[] {
    return this.change('project', message, this.now(), (w, _r, d) => writeField(w, d, field, this.project())) ? ['project'] : [];
  }

  /** `ensureStoryFields`: story time, duration and setting for projects made before they existed. */
  ensureStoryFields(message?: string): string[] {
    const existing = new Set(this.project().fields.map((f) => f.id));
    const order = existing.size;
    const fields: FieldSpec[] = [
      { id: 'story-time', name: 'Story time', type: 'story-time', order },
      { id: 'story-duration', name: 'Duration (hours)', type: 'number', order: order + 1 },
      { id: 'setting', name: 'Setting', type: 'entity-ref', order: order + 2 },
    ].filter((f) => !existing.has(f.id)) as FieldSpec[];
    if (!fields.length) return [];
    const project = this.project();
    return this.change('project', message, this.now(), (w, _r, d) => { for (const f of fields) writeField(w, d, f, project); }) ? ['project'] : [];
  }

  // MARK: Fields (Fields.swift)

  /** `addField(named:type:options:)`: a field at the end of the list, or a removed one of the same name and type brought
   *  back. A list field gets `options` in order. Refuses a name another shown field has. */
  addField(name: string, type: FieldType, options: string[] = [], message?: string): MetadataField {
    const trimmed = fieldNameOf(name);
    const fields = this.project().fields;
    const shown = fieldNamed(visibleFields(fields), trimmed);
    if (shown) throw new ModelError('invalid', `There's already a field called “${shown.name}”.`);
    const removed = fieldNamed(fields.filter((f) => f.hidden && f.type === type), trimmed);
    if (removed) {
      this.setFieldHidden(removed.id, false, message);
      if (canRenameField(removed)) this.renameField(removed.id, trimmed, message);
      for (const o of options) if (!removed.options.some((x) => fieldKey(x.name) === fieldKey(o))) this.addOption(removed.id, o, {}, message);
      return this.project().fields.find((f) => f.id === removed.id)!;
    }
    // A name freed by a rename leaves its id with the renamed field.
    const stable = fieldID(trimmed);
    const id = fields.some((f) => f.id === stable) ? makeID() : stable;
    const seen = new Set<string>();
    const made = options.map((o) => {
      const n = optionNameOf(o);
      if (seen.has(fieldKey(n))) throw new ModelError('invalid', `“${n}” is in the list twice.`);
      seen.add(fieldKey(n));
      return { id: optionID(n, id), name: n };
    });
    const field: FieldSpec = { id, name: trimmed, type, options: type === 'enum' ? made : [], order: Math.max(-1, ...fields.map((f) => f.order)) + 1 };
    this.putField(field, message);
    return this.project().fields.find((f) => f.id === id)!;
  }

  /** `ScrivenerHistory.importHistory`: a Scrivener project's writing history (`ScrivenerPackage.writingHistory`) into this
   *  project, with nothing else — no checkpoint, no merge. A project imported from Scrivener takes only its own
   *  project's history; one written here, or imported another way, takes any. */
  importScrivenerHistory(pkg: { identifier: string; title: string; writingHistory: ImportedDay[] }, message = 'Imported Scrivener’s writing history'): HistorySummary {
    const project = this.project();
    if (!pkg.writingHistory.length) {
      throw new ModelError('invalid', `“${pkg.title}” has no writing history: Scrivener keeps one once words have been written in it.`);
    }
    if (project.source?.startsWith('scrivener:') && project.source !== `scrivener:${pkg.identifier}`) {
      throw new ModelError('invalid', `“${pkg.title}” is a different Scrivener project from the one “${project.title}” was imported from, so its history wasn’t added.`);
    }
    let changed = 0;
    this.change('project', message, this.now(), (w) => { changed = recordImportedHistory(w, pkg.writingHistory); });
    return historySummary(pkg.writingHistory, changed);
  }

  /** `DocumentStore.setScriptFormat`: how a screenplay is laid out — the text is Fountain in every format, so only the
   *  setting changes, and the manuscript arrangement's name follows while it's still the one it was made with
   *  (`MADE_MANUSCRIPT_NAMES`). A kind's own book matter is added by the Apple apps when they next open it. */
  setScriptFormat(format: ScriptFormat, message?: string): string[] {
    const project = this.project();
    if (project.projectType !== 'screenplay') throw new ModelError('invalid', 'Only a script has a script format.');
    if (scriptFormatOf(project.projectType, project.settings) === format) return [];
    const manuscript = project.arrangements.find((a) => a.role === 'manuscript');
    const musical = project.settings.musical === true;
    return this.change('project', message ?? `Set the script format to ${scriptFormatName(format)}`, this.now(), (w) => {
      if (format === 'screen') w.delete(['settings'], 'scriptFormat');
      else w.putIfChanged(['settings'], 'scriptFormat', str(format));
      if (manuscript && MADE_MANUSCRIPT_NAMES.has(manuscript.name)) {
        w.putIfChanged(['arrangements', manuscript.id], 'name', str(scriptManuscriptName(format, musical)));
      }
    }) ? ['project'] : [];
  }

  /** `DocumentStore.setMusical`: makes a play a musical or not (`settings.musical`, stored only while on); the
   *  manuscript's made name follows. The Musical Numbers page is added by the Apple apps when they next open it. */
  setMusical(on: boolean, message?: string): string[] {
    const project = this.project();
    if (!isStageFormat(scriptFormatOf(project.projectType, project.settings))) throw new ModelError('invalid', 'Only a play can be a musical.');
    if (isMusicalOf(project.projectType, project.settings) === on) return [];
    const manuscript = project.arrangements.find((a) => a.role === 'manuscript');
    const format = scriptFormatOf(project.projectType, project.settings);
    return this.change('project', message ?? (on ? 'Make the play a musical' : 'Make the musical a play'), this.now(), (w) => {
      if (on) w.putIfChanged(['settings'], 'musical', bool(true));
      else w.delete(['settings'], 'musical');
      if (manuscript && MADE_MANUSCRIPT_NAMES.has(manuscript.name)) {
        w.putIfChanged(['arrangements', manuscript.id], 'name', str(scriptManuscriptName(format, on)));
      }
    }) ? ['project'] : [];
  }

  /** `renameField`. */
  renameField(id: string, name: string, message?: string): string[] {
    const field = this.requireField(id);
    if (!canRenameField(field)) throw new ModelError('invalid', `“${field.name}” is one of the app's own fields; its name stays.`);
    const trimmed = fieldNameOf(name);
    const other = fieldNamed(visibleFields(this.project().fields), trimmed);
    if (other && other.id !== id) throw new ModelError('invalid', `There's already a field called “${other.name}”.`);
    return this.change('project', message, this.now(), (w) => w.putIfChanged(['metadataSchema', id], 'name', str(trimmed))) ? ['project'] : [];
  }

  /** `moveField(_:after:)`: between `previous` (null: first) and the field after it; fields sharing an order (as imports
   *  leave them) are renumbered. */
  moveField(id: string, previous: string | null, message?: string): string[] {
    this.requireField(id);
    const others = this.project().fields.filter((f) => f.id !== id);
    const found = previous === null ? -1 : others.findIndex((f) => f.id === previous);
    const index = found >= 0 ? found + 1 : 0;
    const before = index > 0 ? others[index - 1].order : undefined;
    const after = index < others.length ? others[index].order : undefined;
    let order: number | undefined;
    if (before !== undefined && after !== undefined) order = before < after ? (before + after) / 2 : undefined;
    else if (before !== undefined) order = before + 1;
    else if (after !== undefined) order = after - 1;
    else order = 0;
    return this.change('project', message, this.now(), (w) => {
      if (order !== undefined) return w.putIfChanged(['metadataSchema', id], 'order', f64(order));
      const ids = others.map((f) => f.id);
      ids.splice(index, 0, id);
      ids.forEach((fid, i) => w.putIfChanged(['metadataSchema', fid], 'order', f64(i)));
    }) ? ['project'] : [];
  }

  /** `setFieldHidden`: a removed field is left out of what the writer sees; its values stay on the documents. */
  setFieldHidden(id: string, hidden: boolean, message?: string): string[] {
    const field = this.requireField(id);
    if (hidden && !canRemoveField(field)) throw new ModelError('invalid', `“${field.name}” is one of the app's own fields, so it stays.`);
    return this.change('project', message, this.now(), (w) => w.putIfChanged(['metadataSchema', id], 'hidden', bool(hidden))) ? ['project'] : [];
  }

  /** `addOption(named:color:to:at:)`: at the end, or at `at` among the options shown. Refuses a name the list has. */
  addOption(fieldID: string, name: string, o: { color?: string; at?: number } = {}, message?: string): FieldOption {
    const field = this.editableOptions(fieldID);
    const trimmed = optionNameOf(name);
    const same = field.options.find((x) => fieldKey(x.name) === fieldKey(trimmed));
    if (same) throw new ModelError('invalid', `“${field.name}” already has “${same.name}”.`);
    const stable = optionID(trimmed, fieldID);
    const option: FieldOption = { id: field.options.some((x) => x.id === stable) ? makeID() : stable, name: trimmed, ...(o.color !== undefined ? { color: o.color } : {}) };
    this.insertOption(fieldID, option, o.at, message);
    return option;
  }

  /** `insertOption(_:into:at:)`: as given (importers keep their own ids). */
  insertOption(fieldID: string, option: FieldOption, at?: number, message?: string): string[] {
    this.requireField(fieldID);
    return this.change('project', message, this.now(), (_w, _r, d) => {
      const list = optionList(d, fieldID);
      insertOptionAt(list, rawIndex(list, at), option);
    }) ? ['project'] : [];
  }

  /** `renameOption`; `checked: false` is for importers, which take the other app's names as they are. */
  renameOption(fieldID: string, id: string, name: string, message?: string, checked = true): string[] {
    const field = this.editableOptions(fieldID);
    const option = requireOption(field, id);
    const trimmed = checked ? optionNameOf(name) : name;
    if (checked) {
      const reason = renameBlocked(field, option);
      if (reason) throw new ModelError('invalid', reason);
      const other = field.options.find((x) => x.id !== id && fieldKey(x.name) === fieldKey(trimmed));
      if (other) throw new ModelError('invalid', `“${field.name}” already has “${other.name}”.`);
    }
    return this.change('project', message, this.now(), (_w, _r, d) => {
      for (const el of optionElements(optionList(d, fieldID), id)) if (String(el.name ?? '') !== trimmed) el.name = new A.ImmutableString(trimmed);
    }) ? ['project'] : [];
  }

  /** `recolorOption`: "r g b" (0…1), or null for none. */
  recolorOption(fieldID: string, id: string, color: string | null, message?: string): string[] {
    requireOption(this.editableOptions(fieldID), id);
    return this.change('project', message, this.now(), (_w, _r, d) => {
      for (const el of optionElements(optionList(d, fieldID), id)) {
        if (color === null) { if (el.color !== undefined) delete el.color; } else if (String(el.color ?? '') !== color || el.color === undefined) el.color = new A.ImmutableString(color);
      }
    }) ? ['project'] : [];
  }

  /** `moveOption(_:in:to:)`: to `index` among the options shown (a status's place in the workflow). */
  moveOption(fieldID: string, id: string, index: number, message?: string): string[] {
    const field = this.editableOptions(fieldID);
    const option = requireOption(field, id);
    if (field.options.indexOf(option) === index) return [];
    return this.change('project', message, this.now(), (_w, _r, d) => {
      const list = optionList(d, fieldID);
      deleteOption(list, id);
      insertOptionAt(list, rawIndex(list, index), option);
    }) ? ['project'] : [];
  }

  /** `removeOption`: documents that had it read as having no value (`fieldDisplay`). */
  removeOption(fieldID: string, id: string, message?: string, checked = true): string[] {
    const field = this.editableOptions(fieldID);
    const option = requireOption(field, id);
    const reason = checked ? removalBlocked(field, option) : null;
    if (reason) throw new ModelError('invalid', reason);
    return this.change('project', message, this.now(), (_w, _r, d) => deleteOption(optionList(d, fieldID), id)) ? ['project'] : [];
  }

  /** `addStarterFields`: the set's fields the project doesn't have yet (by id, or a shown field of the same name); a
   *  removed one is brought back. `name` gives each field's and option's name in the writer's language; colours are the
   *  inks' option colours. Returns the fields added or brought back. */
  addStarterFields(set: StarterFieldSet, name: (english: string) => string = (n) => n, message?: string): MetadataField[] {
    const added: MetadataField[] = [];
    for (const spec of set.fields) {
      const fields = this.project().fields;
      const id = fieldID(spec.name);
      const existing = fields.find((f) => f.id === id);
      if (existing) {
        if (!existing.hidden) continue;
        this.setFieldHidden(id, false, message);
        added.push(existing);
        continue;
      }
      const shown = name(spec.name);
      if (fieldNamed(visibleFields(fields), shown) || fieldNamed(visibleFields(fields), spec.name)) continue;
      const field: FieldSpec = {
        id, name: shown, type: spec.type, order: Math.max(-1, ...fields.map((f) => f.order)) + 1,
        options: (spec.options ?? []).map((o) => ({ id: optionID(o.name, id), name: name(o.name), ...(o.ink ? { color: optionColor(o.ink) } : {}) })),
      };
      this.putField(field, message);
      added.push(this.project().fields.find((f) => f.id === id)!);
    }
    return added;
  }

  // MARK: Outliner views (GridViews.swift)

  /** `addGridView`: the columns, grouping and sort saved as a new view. Throws `ModelError` when the name is empty, too
   *  long, or another view's (a built-in's too). */
  addGridView(view: Omit<GridView, 'id'>, builtInNames: string[] = [], message?: string): GridView {
    const name = normalizedFieldName(view.name);
    const id = gridViewID(name);
    const made: GridView = { ...view, name, id: readGridViews(this.project().settings).some((v) => v.id === id) ? makeID() : id };
    this.putGridView(made, builtInNames, message);
    return made;
  }

  /** `putGridView`: stores a view (new, renamed or updated), committed "View: <name>". */
  putGridView(view: GridView, builtInNames: string[] = [], message?: string): boolean {
    if (isBuiltInView(view)) throw new ModelError('invalid', `“${view.name}” is one of the app’s own views; save it under a name of your own.`);
    const name = normalizedFieldName(view.name);
    if (name === '') throw new ModelError('invalid', 'A view needs a name.');
    if (graphemes(name).length > GRID_VIEW_NAME_LIMIT) throw new ModelError('invalid', `A view’s name can be up to ${GRID_VIEW_NAME_LIMIT} characters.`);
    const other = gridViewNamed(name, readGridViews(this.project().settings));
    if (other && other.id !== view.id) throw new ModelError('invalid', `There’s already a view called “${other.name}”.`);
    if (builtInNames.some((n) => gridViewKey(n) === gridViewKey(name))) throw new ModelError('invalid', `“${name}” is one of the app’s own views.`);
    return this.change('project', message ?? `View: ${name}`, this.now(), (w) => {
      w.ensure(['settings']);
      putJSON(w, ['settings'], GRID_VIEW_PREFIX + view.id, { ...view, name }, decodeGridView, `view “${name}”`, encodeGridView);
    });
  }

  /** `updateGridView`: changes a saved view as it's stored now (another device may have renamed it a moment ago). */
  updateGridView(id: string, patch: Partial<Omit<GridView, 'id'>>, builtInNames: string[] = []): boolean {
    const current = readGridViews(this.project().settings).find((v) => v.id === id);
    return current ? this.putGridView({ ...current, ...patch }, builtInNames) : false;
  }

  deleteGridView(view: Pick<GridView, 'id' | 'name'>): boolean {
    return this.change('project', `View: remove ${view.name}`, this.now(), (w, r) => {
      const settings = r.map(ROOT, 'settings');
      if (settings && r.get(settings, GRID_VIEW_PREFIX + view.id)) w.delete(['settings'], GRID_VIEW_PREFIX + view.id);
    });
  }

  private requireField(id: string): MetadataField {
    const field = this.project().fields.find((f) => f.id === id);
    if (!field) throw new ModelError('notFound', `field ${id}`);
    return field;
  }

  private editableOptions(id: string): MetadataField {
    const field = this.requireField(id);
    if (field.id === 'beat') throw new ModelError('invalid', 'Beats come from the beat sheet. Edit them in Plan ▸ Beats.');
    if (field.type !== 'enum') throw new ModelError('invalid', `“${field.name}” isn't a list.`);
    return field;
  }

  /** `ensureTagsField`: every project has a Tags field — added to one from before it, and a journal's Tags text field
   *  becomes one (the same id and stored values, read as a list). The Mac adds it when a project opens; the web adds it
   *  with the first tag. */
  ensureTagsField(message: string | undefined = 'Add standard fields'): string[] {
    const project = this.project();
    const existing = project.fields.find((f) => f.id === TAGS_FIELD);
    if (existing?.type === 'tags') return [];
    const field: FieldSpec = existing
      ? { id: existing.id, name: existing.name, type: 'tags', options: existing.options, order: existing.order }
      : { id: TAGS_FIELD, name: 'Tags', type: 'tags', order: Math.max(-1, ...project.fields.map((f) => f.order)) + 1 };
    return this.change('project', message, this.now(), (w, _r, d) => writeField(w, d, field, project)) ? ['project'] : [];
  }

  /** `DocumentStore.setTag(_:on:for:)`: a tag added to (or taken off) each of these documents, each keeping the rest of
   *  its tags. Returns how many changed and the documents written. */
  setTag(fragmentIDs: readonly string[], tag: string, on: boolean, field: string = TAGS_FIELD): { count: number; changed: string[] } {
    const changed: string[] = [];
    let count = 0;
    for (const id of new Set(fragmentIDs)) {
      const doc = this.docs.get(fragmentDocName(id));
      if (!doc) continue;
      const tags = fragmentTags(readFragment(doc), field);
      const written = this.setTags(id, on ? addingTag(tag, tags) : removingTag(tag, tags), field);
      if (written.length) { count += 1; changed.push(...written); }
    }
    return { count, changed };
  }

  /** `DocumentStore.tags(in:)`: the tags in use (documents in the Trash aside) and how many documents have each. */
  tagsInUse(field: string = TAGS_FIELD): TagCount[] {
    const project = this.project();
    const lists: string[][] = [];
    for (const [id, entry] of project.fragments) {
      const doc = this.docs.get(fragmentDocName(id));
      if (doc && !entry.trashed) lists.push(fragmentTags(readFragment(doc), field));
    }
    return tagCounts(lists);
  }

  /** `createArrangement` with its nodes (`putNode` each), committed with `message`. Returns the arrangement's id. */
  createArrangement(o: { id?: string; name: string; role: ArrangementRole; order?: number; nodes?: ArrangementNode[] }, message?: string): string {
    const project = this.project();
    const id = o.id ?? this.id();
    this.change('project', message, this.now(), (w) => {
      writeArrangement(w, project, id, o.name, o.role, o.order);
      for (const node of o.nodes ?? []) putNode(w, id, node);
    });
    return id;
  }

  /** `putNode` for each (+ commit with `message`, or none): nodes inserted or updated as given. */
  putNodes(arrangementID: string, nodes: ArrangementNode[], message?: string): string[] {
    this.arrangement(this.project(), arrangementID);
    return this.change('project', message, this.now(), (w) => { for (const node of nodes) putNode(w, arrangementID, node); }) ? ['project'] : [];
  }

  /** `forkArrangement`: a new alternate arrangement with the same structure and the same scenes, joining (or starting)
   *  the source's arrangement set. */
  forkArrangement(arrangementID: string, name: string, originalName = 'Original'): AlternateSet {
    const project = this.project();
    const source = this.arrangement(project, arrangementID);
    const date = this.now();
    const set: AlternateSet = alternateSets(project).find((s) => s.kind === 'arrangement' && s.members.some((m) => m.arrangementID === arrangementID))
      ?? { id: this.id(), kind: 'arrangement', title: source.name, members: [{ id: this.id(), name: originalName, createdAt: date, arrangementID }], currentMemberID: '', role: source.role };
    if (set.currentMemberID === '') set.currentMemberID = set.members[0].id;
    const copy = this.id();
    // Maps stay in Research & Notes, so a fork of it leaves them out (anything under one goes up a level).
    const maps = new Set([...source.nodes.values()].filter((n) => n.fragmentID !== undefined && project.fragments.get(n.fragmentID)?.kind === 'map').map((n) => n.id));
    const parentOfNode = (node: ArrangementNode): string | undefined => {
      let parentID = node.parentID;
      while (parentID !== undefined && maps.has(parentID)) parentID = source.nodes.get(parentID)?.parentID;
      return parentID;
    };
    const ids = new Map<string, string>();
    for (const node of source.nodes.values()) if (!maps.has(node.id)) ids.set(node.id, this.id());
    set.members.push({ id: this.id(), name, createdAt: date, arrangementID: copy });
    this.change('project', `New alternate arrangement “${name}”`, date, (w) => {
      writeArrangement(w, project, copy, `${source.name} — ${name}`, 'alternate');
      for (const node of source.nodes.values()) {
        if (maps.has(node.id)) continue;
        const parentID = parentOfNode(node);
        putNode(w, copy, {
          id: ids.get(node.id)!, fragmentID: node.fragmentID, title: node.title, isFolder: node.isFolder,
          parentID: parentID !== undefined ? ids.get(parentID) : undefined, position: node.position,
        });
      }
      w.putIfChanged(['settings', 'alternates'], set.id, str(suiteJSON(set)));
    });
    return set;
  }

  /** `move(_:in:from:to:parent:index:)`: nodes, with everything under them, into `parent` (null = top level) of `targetID`,
   *  before the `index`th of its children not being moved (or at the end), keeping their reading order. Within one
   *  arrangement they're repositioned; into another they leave the source and are placed afresh (new node ids, the same
   *  documents): out of the Trash a document is no longer trashed, and a note or research document brought into a prose
   *  manuscript becomes a scene. Returns the moved nodes' ids in the target, in order. */
  moveNodes(nodeIDs: string[], sourceID: string, targetID: string, parentID: string | null, index: number | null = null): string[] {
    const project = this.project();
    const source = this.arrangement(project, sourceID);
    const target = this.arrangement(project, targetID);
    if (target.role === 'trash') throw new ModelError('invalid', 'Use Move to Trash to put things in the Trash.');
    if (parentID !== null && !target.nodes.has(parentID)) throw new ModelError('notFound', `folder ${parentID}`);
    const order = new Map<string, number>();
    flattened(source).forEach((e, i) => { if (!order.has(e.node.id)) order.set(e.node.id, i); });
    const requested = new Set(nodeIDs);
    const tops = [...requested].filter((id) => source.nodes.has(id) && !ancestors(source, id).some((a) => requested.has(a.id)))
      .sort((x, y) => (order.get(x) ?? Number.MAX_SAFE_INTEGER) - (order.get(y) ?? Number.MAX_SAFE_INTEGER) || swiftCompare(x, y));
    if (!tops.length) return [];
    const same = sourceID === targetID;
    if (target.role !== 'reference') this.refuseMaps(source, tops, project);
    if (same && tops.some((t) => isWithinSubtree(source, parentID, t))) throw new ModelError('invalid', 'A folder can’t be moved into itself.');
    const siblings = childrenOf(target, parentID).filter((n) => !(same && tops.includes(n.id)));
    const slot = Math.max(0, Math.min(index ?? siblings.length, siblings.length));
    let lower = slot > 0 ? siblings[slot - 1].position : undefined;
    const upper = slot < siblings.length ? siblings[slot].position : undefined;
    const nextPosition = () => {
      const position = between(lower, upper);
      lower = position;
      return position;
    };
    const placed: string[] = [];
    const now = this.now();
    const becomeProse: string[] = [];
    this.change('project', same ? 'Moved' : `Moved to ${target.name}`, now, (w) => {
      if (same) {
        for (const id of tops) {
          const node = source.nodes.get(id);
          if (!node) continue;
          putNode(w, targetID, { ...node, parentID: parentID ?? undefined, position: nextPosition() });
          placed.push(id);
        }
        return;
      }
      const fromTrash = source.role === 'trash';
      const intoProse = target.role === 'manuscript' && manuscriptKind(project.projectType) === 'prose';
      for (const top of tops) {
        const newIDs = new Map<string, string>();
        for (const node of subtree(source, top)) {
          const newID = this.id();
          newIDs.set(node.id, newID);
          const parent = node.id === top ? (parentID ?? undefined) : (() => { const p = parentOf(source, node.id); return p ? newIDs.get(p.id) : undefined; })();
          putNode(w, targetID, { id: newID, fragmentID: node.fragmentID, title: node.title, isFolder: node.isFolder, parentID: parent,
            position: node.id === top ? nextPosition() : node.position });
          const fid = node.fragmentID;
          const entry = fid !== undefined ? project.fragments.get(fid) : undefined;
          if (fid === undefined || !entry) continue;
          if (fromTrash) w.putIfChanged(['fragments', fid], 'trashed', bool(false));
          if (intoProse && (entry.kind === 'note' || entry.kind === 'research')) {
            w.putIfChanged(['fragments', fid], 'kind', str('prose'));
            becomeProse.push(fid);
          }
        }
        // `removeNode`: the node and its subtree.
        for (const n of subtree(source, top)) w.delete(['arrangements', sourceID, 'nodes'], n.id);
        const id = newIDs.get(top);
        if (id) placed.push(id);
      }
    });
    for (const fid of becomeProse) {
      if (this.docs.has(fragmentDocName(fid))) this.change(fragmentDocName(fid), 'Moved into the manuscript', now, (w) => w.putIfChanged([], 'kind', str('prose')));
    }
    return placed;
  }

  /** `putEntity` (+ commit with `message`): a new entity takes the least-used colour of its kind; the study page link
   *  and a colour are never cleared. */
  putEntity(e: StoryEntity, message?: string): string[] {
    const project = this.project();
    const isNew = !project.entities.some((x) => x.id === e.id);
    const assigned = e.color ?? (isNew ? nextEntityColor(e.kind, project.entities) : undefined);
    return this.change('project', message, this.now(), (w) => writeEntity(w, e, assigned)) ? ['project'] : [];
  }

  // MARK: Character studies

  /** `ensureStudyPages(in:)`: study pages for every kind — what project load, `add_entity` and `suite entity` run. */
  ensureStudyPages(date: Date = this.now()): boolean {
    let changed = false;
    for (const kind of ['character', 'place'] as const) changed = this.ensureStudyPagesOf(kind, date) || changed;
    return changed;
  }

  /** Characters' pages only. */
  ensureCharacterStudies(date: Date = this.now()): boolean {
    return this.ensureStudyPagesOf('character', date);
  }

  /** `ensureStudyPages(_:in:)`: every entity of the kind without one gets a study page in the kind's folder at the top of
   *  Research & Notes (Characters first, Places after it; the writer's own folder of that name adopted, a same-named
   *  sketch in it linked). Each entity gets a page once: a page the writer deletes doesn't come back. */
  ensureStudyPagesOf(studyKind: StudyKind, date: Date = this.now()): boolean {
    const CharacterStudy = STUDY_KINDS[studyKind];
    let project = this.project();
    const setting = project.settings[CharacterStudy.settingsKey];
    const enabled = typeof setting === 'boolean' ? setting : project.projectType !== 'journal';
    if (!enabled) return false;
    const pending = project.entities.filter((e) => e.kind === CharacterStudy.entityKind && e.studyID === undefined && e.name !== '');
    if (!pending.length) return false;
    const projectID = project.id;

    const reference = project.arrangements.find((a) => a.role === 'reference');
    const referenceID = reference?.id ?? bookMatterStableID(projectID, 'arrangement');
    const nodes = new Map(reference?.nodes ?? []);
    const live = (): Arrangement => ({ id: referenceID, name: reference?.name ?? 'Research & Notes', role: 'reference', order: reference?.order ?? 0, nodes });
    const fragmentTitle = (id: string) => {
      const doc = this.docs.get(fragmentDocName(id));
      return doc ? (new SwiftReader(doc).string(ROOT, 'title') ?? '') : undefined;
    };
    const title = (node: ArrangementNode) => (node.fragmentID !== undefined ? fragmentTitle(node.fragmentID) : undefined) ?? node.title ?? '';
    const metadataOf = (id: string) => {
      const doc = this.docs.get(fragmentDocName(id));
      return doc ? readMetadata(new SwiftReader(doc)) : null;
    };
    const projectWrites: ((w: Writer) => void)[] = [];
    if (!reference) projectWrites.push((w) => writeArrangement(w, project, referenceID, 'Research & Notes', 'reference'));

    const topLevel = childrenOf(live(), null);
    const ourFolderNode = CharacterStudy.stableID(projectID, 'folder-node');
    let folderNodeID: string;
    const existing = topLevel.find((n) => n.isFolder && CharacterStudy.folderNames.has(letterKey(title(n))));
    if (nodes.has(ourFolderNode)) {
      folderNodeID = ourFolderNode;
    } else if (existing) {
      folderNodeID = existing.id;
      if (existing.fragmentID !== undefined && this.docs.has(fragmentDocName(existing.fragmentID))) {
        this.change(fragmentDocName(existing.fragmentID), undefined, date, (w) => w.putIfChanged(['metadata'], CharacterStudy.field, str(STUDY_FOLDER_MARKER)));
      }
    } else {
      const isBookMatter = (n: ArrangementNode) => n.fragmentID !== undefined && metadataOf(n.fragmentID)?.[BOOK_MATTER_FIELD]?.type === 'text';
      let after = [...topLevel].reverse().find(isBookMatter)?.position;
      if (studyKind === 'place') {
        // After the Characters folder (ours, or an adopted one), when there is one.
        const isCharacters = (n: ArrangementNode) => n.id === STUDY_KINDS.character.stableID(projectID, 'folder-node')
          || (n.fragmentID !== undefined && metadataOf(n.fragmentID)?.[STUDY_KINDS.character.field]?.value === STUDY_FOLDER_MARKER);
        const characters = topLevel.find(isCharacters);
        if (characters) after = characters.position;
      }
      const before = after !== undefined ? topLevel.map((n) => n.position).find((p) => p > after) : topLevel[0]?.position;
      const folderID = CharacterStudy.stableID(projectID, 'folder');
      const fill = (w: Writer, d: Record<string, any>) => {
        setTextIn(d, 'synopsis', CharacterStudy.folderSynopsis);
        w.putIfChanged(['metadata'], CharacterStudy.field, str(STUDY_FOLDER_MARKER));
        w.putIfChanged([], 'includeInCompile', bool(false));
      };
      if (this.docs.has(fragmentDocName(folderID))) {
        this.change(fragmentDocName(folderID), `Add ${CharacterStudy.folderTitle} folder`, date, (w, _r, d) => fill(w, d));
      } else {
        this.createStableFragment({ id: folderID, kind: 'note', title: CharacterStudy.folderTitle, message: `Add ${CharacterStudy.folderTitle} folder`, date }, fill, (x) => projectWrites.push(x));
      }
      folderNodeID = ourFolderNode;
      const node: ArrangementNode = { id: folderNodeID, fragmentID: folderID, isFolder: true, position: between(after, before) };
      nodes.set(node.id, node);
      projectWrites.push((w) => putNode(w, referenceID, node));
    }

    project = this.project();
    const entities: StoryEntity[] = [];
    for (const character of pending) {
      const inside = childrenOf(live(), folderNodeID);
      const names = new Set([character.name, ...character.aliases].map(letterKey));
      const entity: StoryEntity = { ...character };
      const adopted = inside.find((n) => !n.isFolder && n.fragmentID !== undefined && names.has(letterKey(title(n))));
      const isStudy = adopted?.fragmentID !== undefined && studyPageOf(metadataOf(adopted.fragmentID) ?? {}) !== null;
      if (adopted?.fragmentID !== undefined && this.docs.has(fragmentDocName(adopted.fragmentID)) && !isStudy) {
        // The writer's own sketch, e.g. from Scrivener: linked, its text untouched.
        const adoptedID = adopted.fragmentID;
        this.change(fragmentDocName(adoptedID), `Link study page to ${character.name}`, date, (w, r, d) => {
          w.putIfChanged(['metadata'], CharacterStudy.field, str(character.id));
          if ((r.string(ROOT, 'text') ?? '').trim() === '') setTextIn(d, 'text', CharacterStudy.template);
        });
        entity.studyID = adoptedID;
      } else {
        const id = CharacterStudy.fragmentID(projectID, character.id);
        if (!this.docs.has(fragmentDocName(id))) {
          this.createStableFragment({ id, kind: 'prose', title: character.name, message: `Add study page for ${character.name}`, date }, (w, d) => {
            setTextIn(d, 'text', CharacterStudy.template);
            setTextIn(d, 'synopsis', CharacterStudy.guidance(character.name));
            w.putIfChanged(['metadata'], CharacterStudy.field, str(character.id));
            w.putIfChanged([], 'includeInCompile', bool(false));
          }, (x) => projectWrites.push(x));
          // Before the first page that sorts after the name (alphabetical where the folder is).
          let at = inside.findIndex((n) => title(n).localeCompare(character.name, undefined, { sensitivity: 'accent' }) > 0);
          if (at < 0) at = inside.length;
          const node: ArrangementNode = {
            id: CharacterStudy.stableID(projectID, `node|${character.id}`), fragmentID: id, isFolder: false, parentID: folderNodeID,
            position: between(at > 0 ? inside[at - 1].position : undefined, inside[at]?.position),
          };
          nodes.set(node.id, node);
          projectWrites.push((w) => putNode(w, referenceID, node));
        }
        entity.studyID = id;
      }
      entities.push(entity);
    }
    this.change('project', `Add ${CharacterStudy.entityKind} study pages`, date, (w) => {
      for (const write of projectWrites) write(w);
      for (const e of entities) writeEntity(w, e, e.color ?? (project.entities.some((x) => x.id === e.id) ? undefined : nextEntityColor(e.kind, project.entities)));
    });
    return true;
  }

  /** `FragmentDocument.addAttachment` + commit: the file added to the end of the document's `attachments` list (left
   *  alone when it's already there), with a line for its notes when given (Look Up's Keep writes the picture's credit).
   *  The bytes go to the relay as an `attachment.<hmac>` snapshot by the app (`uploadAttachments`). */
  addAttachment(fragmentID: string, ref: { sha256: string; filename: string; mimeType: string }, o: { noteLine?: string; message?: string } = {}): string[] {
    const name = fragmentDocName(fragmentID);
    if (!this.docs.has(name)) throw new ModelError('notFound', `document ${fragmentID}`);
    const changed = this.change(name, o.message ?? 'Add an attachment', this.now(), (_w, r, d) => {
      if (!Array.isArray(d.attachments)) d.attachments = [];
      if (!d.attachments.some((a: any) => a?.sha256 === ref.sha256)) d.attachments.push({ sha256: ref.sha256, filename: ref.filename, mimeType: ref.mimeType });
      const line = o.noteLine;
      if (line !== undefined) {
        const notes = r.string(ROOT, 'notes') ?? '';
        if (!notes.includes(line)) setTextIn(d, 'notes', notes === '' ? line : notes + '\n' + line);
      }
      d.modifiedAt = this.now();
    });
    return changed ? [name] : [];
  }

  // MARK: Book matter (BookMatter.swift)

  /** `ensureBookMatter` for a project that has never had book matter and has none of its own to adopt (a new one, as the
   *  Word import makes): the Front Matter, Back Matter and Submission folders (or the kind's: `BookMatter.folders`) with
   *  their documents, in the Publishing arrangement just after the manuscript, and Research & Notes if there's none. Ids,
   *  documents (`createStableFragment`) and places are the Mac's, and `settings.bookMatter` says it's done, so an Apple
   *  device opening the project adds nothing. Adopting a Scrivener import's own folders stays with the Apple apps.
   *  Returns whether anything changed. */
  addBookMatter(date: Date = this.now()): boolean {
    const project = this.project();
    if (typeof project.settings.bookMatter === 'number' && project.settings.bookMatter >= 1) return false;
    const projectID = project.id;
    const kind = projectKind(project.projectType, scriptFormatOf(project.projectType, project.settings));
    const musical = isMusicalOf(project.projectType, project.settings);
    const projectWrites: ((w: Writer) => void)[] = [];
    if (!project.arrangements.some((a) => a.role === 'reference')) {
      projectWrites.push((w) => writeArrangement(w, project, bookMatterStableID(projectID, 'arrangement'), 'Research & Notes', 'reference'));
    }
    // Publishing: just after the manuscript (`publishingArrangement`), counting the Research & Notes made above.
    let publishingID = project.arrangements.find((a) => a.role === 'publishing')?.id;
    if (!publishingID) {
      const orders = project.arrangements.map((a) => a.order);
      if (!project.arrangements.some((a) => a.role === 'reference')) orders.push(Math.max(-1, ...orders) + 1);
      const manuscript = project.arrangements.find((a) => a.role === 'manuscript')?.order ?? 0;
      const later = orders.filter((o) => o > manuscript);
      const next = later.length ? Math.min(...later) : manuscript + 1;
      const id = bookMatterStableID(projectID, 'publishing-arrangement');
      publishingID = id;
      projectWrites.push((w) => writeArrangement(w, project, id, 'Publishing', 'publishing', (manuscript + next) / 2));
    }
    const arrangementID = publishingID;
    const existing = project.arrangements.find((a) => a.id === arrangementID);
    const topLevel = existing ? childrenOf(existing, null) : [];

    const make = (matter: BookMatter, text: string) => {
      const id = BookMatter.stableID(matter, projectID);
      if (this.docs.has(fragmentDocName(id))) return id;
      this.createStableFragment({ id, kind: BookMatter.placement(matter) === 'folder' ? 'note' : 'prose', title: BookMatter.title(matter), message: `Add ${BookMatter.title(matter)}`, date }, (w, d) => {
        setTextIn(d, 'text', text);
        setTextIn(d, 'synopsis', BookMatter.guidance(matter, kind));
        w.putIfChanged(['metadata'], BOOK_MATTER_FIELD, str(matter));
        w.putIfChanged([], 'includeInCompile', bool(BookMatter.compiled(matter)));
      }, (x) => projectWrites.push(x));
      return id;
    };
    // The folders go together, each straight after the one before it, the first before anything already there; their
    // documents in book order.
    let previous: string | undefined;
    const placed: string[] = topLevel.map((n) => n.position);
    for (const folder of BookMatter.folders(kind)) {
      const next = previous !== undefined ? placed.filter((p) => p > previous!).sort()[0] : placed.slice().sort()[0];
      const position = between(previous, next);
      placed.push(position);
      previous = position;
      const folderNodeID = BookMatter.stableID(folder, projectID, 'node');
      const folderNode: ArrangementNode = { id: folderNodeID, fragmentID: make(folder, ''), isFolder: true, position };
      projectWrites.push((w) => putNode(w, arrangementID, folderNode));
      let last: string | undefined;
      for (const matter of BookMatter.documents(folder, kind, musical)) {
        const node: ArrangementNode = { id: BookMatter.stableID(matter, projectID, 'node'), fragmentID: make(matter, BookMatter.template(matter)), isFolder: false,
          parentID: folderNodeID, position: between(last, undefined) };
        last = node.position;
        projectWrites.push((w) => putNode(w, arrangementID, node));
      }
    }
    this.change('project', 'Add front matter, back matter and submission documents', date, (w) => {
      for (const write of projectWrites) write(w);
      if (kind === 'stage-play') w.putIfChanged(['settings'], 'stageMatter', int(1));
      if (kind === 'radio-play') w.putIfChanged(['settings'], 'radioMatter', int(1));
      if (musical) w.putIfChanged(['settings'], 'musicalMatter', int(1));
      if (isScriptKind(kind)) w.putIfChanged(['settings'], 'matterWords', int(1));
      w.putIfChanged(['settings'], 'bookMatter', int(BOOK_MATTER_VERSION));
    });
    return true;
  }

  // MARK: Maps (DocumentStore+Maps.swift)

  /** `researchArrangementID(in:)`: Research & Notes, made on first use with the stable id book matter and character
   *  studies use ("Add Research & Notes"), so two devices making it before they sync get one arrangement. */
  researchArrangementID(): string {
    const project = this.project();
    const reference = project.arrangements.find((a) => a.role === 'reference');
    if (reference) return reference.id;
    return this.createArrangement({ id: bookMatterStableID(project.id, 'arrangement'), name: 'Research & Notes', role: 'reference' }, 'Add Research & Notes');
  }

  /** `mapsFolder`: the Mind Maps folder at the top of Research & Notes, made with the first map. If the writer has put it in
   *  the Trash, new maps go at the top of Research & Notes instead (the folder isn't brought back). */
  mapsFolder(): { arrangementID: string; nodeID: string | null } {
    const referenceID = this.researchArrangementID();
    const project = this.project();
    const nodeID = Maps.folderNodeID(project.id);
    for (const a of project.arrangements) if (a.role === 'reference' && a.nodes.has(nodeID)) return { arrangementID: a.id, nodeID };
    const folderID = Maps.folderID(project.id);
    if (project.fragments.has(folderID)) return { arrangementID: referenceID, nodeID: null };
    const date = this.now();
    const writes: ((w: Writer) => void)[] = [];
    this.createStableFragment({ id: folderID, kind: 'note', title: Maps.folderTitle, message: 'Add Mind Maps folder', date }, (w, d) => {
      setTextIn(d, 'synopsis', Maps.folderSynopsis);
      w.putIfChanged([], 'includeInCompile', bool(false));
    }, (write) => writes.push(write));
    const { parent, position } = this.slot(this.arrangement(project, referenceID), { inside: null });
    this.change('project', 'Add Mind Maps folder', date, (w) => {
      for (const write of writes) write(w);
      putNode(w, referenceID, { id: nodeID, fragmentID: folderID, isFolder: true, parentID: parent ?? undefined, position });
    });
    return { arrangementID: referenceID, nodeID };
  }

  /** `newMap`: a map in the Maps folder, unless `placement` says where in a Research & Notes arrangement. It starts with
   *  one note holding the project's title, made the centre, or with `content` (an imported board). */
  newMap(o: { title: string; content?: MapContent; placement?: Placement; arrangementID?: string; date?: Date }): { fragmentID: string; nodeID: string } {
    const project = this.project();
    let place: { arrangementID: string; placement: Placement };
    if (o.placement && o.arrangementID) {
      if (this.arrangement(project, o.arrangementID).role !== 'reference') throw new ModelError('invalid', Maps.staysInResearch);
      place = { arrangementID: o.arrangementID, placement: o.placement };
    } else {
      const folder = this.mapsFolder();
      place = { arrangementID: folder.arrangementID, placement: { inside: folder.nodeID } };
    }
    const date = o.date ?? this.now();
    const fragmentID = this.id();
    const title = oneLineTitle(o.title === '' ? 'Map' : o.title);
    const centre = o.content ? null : mapNote({ id: this.id(), text: project.title, at: { x: 0, y: 0 }, size: 'large', createdAt: date });
    const actor = this.context.newActor?.() ?? this.context.actor;
    const message = o.content ? 'Imported map' : 'New map';
    const doc = A.change(A.init<Record<string, any>>(actor ? { actor } : undefined), { message, time: changeTime(date) }, (d) => {
      writeFragmentKeys(new Writer(d, new SwiftReader(A.init())), d, { id: fragmentID, projectID: project.id, kind: 'map', title, createdAt: date });
      const map = new MapDocument(d, () => this.id());
      if (o.content) {
        map.replaceMapContent(o.content);
      } else if (centre) {
        map.addMapNote(centre);
        map.setMapCentre(centre.id);
        map.setMapNote(centre.id, { outline: 'oval' });
      }
    });
    this.docs.set(fragmentDocName(fragmentID), doc as A.Doc<unknown>);
    this.written.add(doc);
    const nodeID = this.id();
    const { parent, position } = this.slot(this.arrangement(this.project(), place.arrangementID), place.placement);
    this.change('project', `New map “${title}”`, date, (w) => {
      registerFragment(w, { id: fragmentID, kind: 'map', trashed: false, createdAt: date });
      putNode(w, place.arrangementID, { id: nodeID, fragmentID, isFolder: false, parentID: parent ?? undefined, position });
    });
    return { fragmentID, nodeID };
  }

  /** `editMap`: changes a map, refused under lock, committed (with `message`, a named history entry, or none) with its
   *  `modifiedAt`. Lines left pointing at a deleted note are tidied as part of it. */
  editMap<T>(fragmentID: string, change: (map: MapDocument) => T, o: { message?: string; date?: Date } = {}): T {
    const r = new SwiftReader(this.fragmentDoc(fragmentID));
    if (r.string(ROOT, 'kind') !== 'map') throw new ModelError('invalid', `“${r.string(ROOT, 'title') ?? ''}” isn't a map.`);
    this.requireUnlocked([fragmentID]);
    const date = o.date ?? this.now();
    let result: T | undefined;
    this.change(fragmentDocName(fragmentID), o.message, date, (w, _r, d) => {
      const map = new MapDocument(d, () => this.id());
      result = change(map);
      map.tidyMapLines();
      w.putIfChanged([], 'modifiedAt', timestamp(date));
    });
    return result as T;
  }

  /** `turnMapNotesIntoScenes` (MapProject.swift): turns notes into documents in the manuscript (after a "Before turning
   *  notes into scenes" checkpoint) — at the end of a manuscript folder (a node), at the top level, or in a new folder at
   *  the end of the manuscript — then each note into a card linked to its document (outlined as a box unless it had an
   *  outline). Returns each note's new document. */
  turnMapNotesIntoScenes(mapID: string, plan: readonly MapScenesItem[], destination: MapScenesDestination, o: { date?: Date } = {}): Map<string, string> {
    const made = new Map<string, string>();
    if (!plan.length) return made;
    const project = this.project();
    this.requireUnlocked([mapID]);
    const manuscript = project.arrangements.find((a) => a.role === 'manuscript');
    if (!manuscript) throw new ModelError('invalid', 'This project has no manuscript.');
    const kind = manuscriptKind(project.projectType);
    this.createCheckpoint('Before turning notes into scenes', 'manual');
    let parent: string | null;
    if ('folder' in destination) {
      if (!manuscript.nodes.has(destination.folder)) throw new ModelError('notFound', `folder ${destination.folder}`);
      parent = destination.folder;
    } else if ('newFolder' in destination) {
      const name = trimCFWhitespaceAndNewlines(destination.newFolder);
      parent = this.newFragment({ arrangementId: manuscript.id, placement: { inside: null }, title: name === '' ? 'New Chapter' : name, kind, isFolder: true, post: false }).nodeId;
    } else {
      parent = null;
    }
    const make = (item: MapScenesItem, inside: string | null) => {
      const ids = this.newFragment({ arrangementId: manuscript.id, placement: { inside }, title: item.title === '' ? 'Untitled' : item.title, kind, isFolder: item.children.length > 0, post: false });
      if (item.synopsis !== '') this.writeFragment(ids.fragmentId, { synopsis: item.synopsis }, 'Synopsis from a map');
      made.set(item.noteID, ids.fragmentId);
      for (const child of item.children) make(child, ids.nodeId);
    };
    for (const item of plan) make(item, parent);
    this.editMap(mapID, (map) => {
      const content = map.mapContent();
      for (const [note, fragment] of [...made].sort(([a], [b]) => swiftCompare(a, b))) {
        map.setMapNote(note, { link: { kind: 'fragment', id: fragment } });
        if (content.notes.find((n) => n.id === note)?.outline === 'none') map.setMapNote(note, { outline: 'box' });
      }
    }, { message: 'Turned notes into scenes', date: o.date });
    return made;
  }

  // MARK: Notes for Assistants (AgentBrief.swift)

  /** `agentBrief(in:)`: the project's Notes for Assistants, if it has one that isn't in the Trash — the stable one first,
   *  then any document carrying the marker. */
  agentBrief(): string | null {
    const project = this.project();
    const metadataOf = (id: string) => {
      const doc = this.docs.get(fragmentDocName(id));
      return doc ? readMetadata(new SwiftReader(doc)) : null;
    };
    const stable = AgentBrief.fragmentID(project.id);
    const entry = project.fragments.get(stable);
    if (entry && !entry.trashed) {
      const m = metadataOf(stable);
      if (m && isAgentBrief(m)) return stable;
    }
    for (const [id, e] of [...project.fragments].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))) {
      if (e.trashed || e.kind === 'map') continue;
      const m = metadataOf(id);
      if (m && isAgentBrief(m)) return id;
    }
    return null;
  }

  /** `ensureAgentBrief`: the brief, made if the project has none — at the top of Research & Notes (made too if need be),
   *  a note with the template, Include in Compile off; one the writer trashed is put back rather than made again. Returns
   *  where it is. */
  ensureAgentBrief(date: Date = this.now()): { fragmentId: string; nodeId: string; arrangementId: string } {
    let project = this.project();
    const projectID = project.id;
    const place = (id: string) => {
      const p = this.project();
      const a = p.arrangements.find((x) => x.role === 'reference')!;
      const node = [...a.nodes.values()].find((n) => n.fragmentID === id)!;
      return { fragmentId: id, nodeId: node.id, arrangementId: a.id };
    };
    const existing = this.agentBrief();
    const reference = project.arrangements.find((a) => a.role === 'reference');
    const referenceID = reference?.id ?? bookMatterStableID(projectID, 'arrangement');
    if (existing !== null && reference) return place(existing);
    if (!reference) this.change('project', 'Add Research & Notes', date, (w) => writeArrangement(w, project, referenceID, 'Research & Notes', 'reference'));
    if (existing !== null) return place(existing);
    project = this.project();
    const id = AgentBrief.fragmentID(projectID);
    const first = childrenOf(this.arrangement(project, referenceID), null)[0]?.position;
    const node: ArrangementNode = { id: AgentBrief.nodeID(projectID), fragmentID: id, isFolder: false, position: between(undefined, first) };
    if (this.docs.has(fragmentDocName(id))) {
      // Trashed, or taken out of every arrangement: back to the top of Research & Notes.
      this.change(fragmentDocName(id), 'Restore Notes for Assistants', date, (w) => w.putIfChanged(['metadata'], AgentBrief.field, str(AgentBrief.marker)));
      const trash = project.arrangements.find((a) => a.role === 'trash');
      const trashed = trash ? [...trash.nodes.values()].find((n) => n.fragmentID === id) : undefined;
      if (trash && trashed) {
        this.moveNodes([trashed.id], trash.id, referenceID, null, 0);
      } else if (!project.arrangements.some((a) => [...a.nodes.values()].some((n) => n.fragmentID === id))) {
        this.change('project', 'Restore Notes for Assistants', date, (w) => {
          putNode(w, referenceID, node);
          registerFragment(w, { id, kind: project.fragments.get(id)?.kind ?? 'note', trashed: false, createdAt: project.fragments.get(id)?.createdAt ?? date });
        });
      }
      return place(id);
    }
    // Built the same on every device (`createStableFragment`), so two that add it before syncing merge.
    const projectWrites: ((w: Writer) => void)[] = [];
    this.createStableFragment({ id, kind: 'note', title: AgentBrief.title, message: 'Add Notes for Assistants', date }, (w, d) => {
      setTextIn(d, 'text', AgentBrief.template);
      setTextIn(d, 'synopsis', AgentBrief.synopsis);
      w.putIfChanged(['metadata'], AgentBrief.field, str(AgentBrief.marker));
      w.putIfChanged([], 'includeInCompile', bool(false));
    }, (x) => projectWrites.push(x));
    this.change('project', 'Add Notes for Assistants', date, (w) => {
      for (const write of projectWrites) write(w);
      putNode(w, referenceID, node);
    });
    return place(id);
  }

  // MARK: Drafts (Drafts.swift)

  /** `inboxFolder`: the Inbox folder at the top of Research & Notes (the manuscript if there's none), made on first use. */
  inboxFolder(): { arrangementID: string; nodeID: string } {
    const project = this.project();
    const arrangement = project.arrangements.find((a) => a.role === 'reference') ?? project.arrangements.find((a) => a.role === 'manuscript');
    if (!arrangement) throw new ModelError('notFound', 'an arrangement to hold the inbox');
    const title = (id: string) => { const doc = this.docs.get(fragmentDocName(id)); return doc ? new SwiftReader(doc).string(ROOT, 'title') : undefined; };
    const inbox = [...arrangement.nodes.values()].find((n) => n.parentID === undefined && n.isFolder && n.fragmentID !== undefined && title(n.fragmentID) === INBOX_TITLE);
    if (inbox) return { arrangementID: arrangement.id, nodeID: inbox.id };
    const made = this.newFragment({ arrangementId: arrangement.id, placement: { inside: null }, title: INBOX_TITLE, kind: 'note', isFolder: true, post: false });
    return { arrangementID: arrangement.id, nodeID: made.nodeId };
  }

  /** `addDraft`: a new document at the end of a manuscript folder, or in the Inbox, with its synopsis and notes
   *  ("Draft notes"). `text` is stored as given (the prose dialect, or Fountain in a screenplay). Returns its id. */
  addDraft(o: { title: string; text: string; synopsis?: string | null; notes: string; folderNodeID?: string | null; afterNodeID?: string | null }): string {
    const project = this.project();
    const kind = manuscriptKind(project.projectType);
    let place: { arrangementID: string; placement: Placement };
    if (o.afterNodeID) {
      // Right after an item in the manuscript, under the same parent.
      const manuscript = project.arrangements.find((a) => a.role === 'manuscript');
      if (!manuscript || !manuscript.nodes.has(o.afterNodeID)) throw new ModelError('invalid', 'That isn’t an item in the manuscript.');
      place = { arrangementID: manuscript.id, placement: { after: o.afterNodeID } };
    } else if (o.folderNodeID) {
      const manuscript = project.arrangements.find((a) => a.role === 'manuscript');
      if (!manuscript || manuscript.nodes.get(o.folderNodeID)?.isFolder !== true) throw new ModelError('invalid', 'That isn’t a folder in the manuscript.');
      place = { arrangementID: manuscript.id, placement: { inside: o.folderNodeID } };
    } else {
      const inbox = this.inboxFolder();
      place = { arrangementID: inbox.arrangementID, placement: { inside: inbox.nodeID } };
    }
    const made = this.newFragment({ arrangementId: place.arrangementID, placement: place.placement, title: o.title, kind, text: o.text, post: false });
    this.writeFragment(made.fragmentId, { ...(o.synopsis ? { synopsis: o.synopsis } : {}), notes: o.notes }, 'Draft notes');
    return made.fragmentId;
  }

  // MARK: Fragment writes (FragmentDocument setters + commit)

  /** `setText`/`setSynopsis`/`setNotes` (a minimal diff, so anchors hold), `setTitle`, then `commit(message)`; no
   *  `modifiedAt`, as the Swift's direct setters leave it. */
  writeFragment(fragmentID: string, o: { text?: string; synopsis?: string; notes?: string; title?: string }, message?: string): string[] {
    const name = fragmentDocName(fragmentID);
    this.fragmentDoc(fragmentID);
    return this.change(name, message, this.now(), (w, _r, d) => {
      if (o.title !== undefined) w.putIfChanged([], 'title', str(o.title));
      for (const key of ['text', 'synopsis', 'notes'] as const) if (o[key] !== undefined) setTextIn(d, key, o[key]!);
    }) ? [name] : [];
  }

  /** `addComment` (on the text) or `addSynopsisComment`: anchored to a scalar range of the current text (clamped), at
   *  the document's heads, committed with `message`. */
  addMarginComment(fragmentID: string, o: {
    on?: 'text' | 'synopsis'; range: [number, number]; body: string; author: string; authorKind: 'human' | 'agent';
    id?: string; parentID?: string | null; message?: string; category?: string | null;
  }): WrittenComment {
    const doc = this.fragmentDoc(fragmentID);
    const key = o.on === 'synopsis' ? 'synopsis' : 'text';
    const current = new SwiftReader(doc).string(ROOT, key) ?? '';
    const count = scalarLength(current);
    const lower = Math.min(Math.max(0, o.range[0]), count);
    const upper = Math.min(Math.max(0, o.range[1]), count);
    const comment: WrittenComment = { id: o.id ?? this.id(), quote: sliceScalars(current, lower, Math.max(lower, upper)), anchorStart: lower, anchorEnd: upper, anchorHeads: encodeHeads(A.getHeads(doc)) };
    const createdAt = this.now();
    this.change(fragmentDocName(fragmentID), o.message, createdAt, (w) => writeComment(w, key === 'synopsis' ? 'synopsisComments' : 'comments', {
      ...comment, author: o.author, authorKind: o.authorKind, body: o.body, parentID: o.parentID ?? null, status: 'open', createdAt,
      category: o.category,
    }));
    return comment;
  }

  /** `addSuggestion`: proposes replacing a scalar range of the current text; the text itself never changes. */
  addSuggestion(fragmentID: string, o: { range: [number, number]; proposedText: string; note?: string; author: string; authorKind: 'human' | 'agent'; id?: string; message?: string }):
    WrittenComment & { proposedText: string } {
    const doc = this.fragmentDoc(fragmentID);
    const text = new SwiftReader(doc).string(ROOT, 'text') ?? '';
    const count = scalarLength(text);
    const lower = Math.min(Math.max(0, o.range[0]), count), upper = Math.min(Math.max(0, o.range[1]), count);
    const id = o.id ?? this.id();
    const s = { id, quote: sliceScalars(text, lower, Math.max(lower, upper)), anchorStart: lower, anchorEnd: upper, anchorHeads: encodeHeads(A.getHeads(doc)), proposedText: o.proposedText };
    const createdAt = this.now();
    this.change(fragmentDocName(fragmentID), o.message, createdAt, (w, _r, d) => {
      if (!d.suggestions) d.suggestions = {};
      const path = ['suggestions', id];
      w.put(path, 'author', str(o.author));
      w.put(path, 'authorKind', str(o.authorKind));
      w.put(path, 'anchorStart', { t: 'uint', v: s.anchorStart });
      w.put(path, 'anchorEnd', { t: 'uint', v: s.anchorEnd });
      w.put(path, 'anchorHeads', str(s.anchorHeads));
      w.put(path, 'quote', str(s.quote));
      w.put(path, 'proposedText', str(s.proposedText));
      w.put(path, 'note', str(o.note ?? ''));
      w.put(path, 'status', str('open'));
      w.put(path, 'createdAt', timestamp(createdAt));
    });
    return s;
  }

  /** `addTagProposal` for each, one change: proposals the writer accepts or rejects (`TagProposal.synopsisField` =
   *  "@synopsis" proposes a synopsis). Returns their ids. */
  addTagProposals(fragmentID: string, proposals: { author: string; field: string; value: string; note?: string }[], message?: string): string[] {
    this.fragmentDoc(fragmentID);
    const createdAt = this.now();
    const ids = proposals.map(() => this.id());
    this.change(fragmentDocName(fragmentID), message, createdAt, (w) => {
      proposals.forEach((p, i) => {
        const path = ['tagProposals', ids[i]];
        w.put(path, 'author', str(p.author));
        w.put(path, 'field', str(p.field));
        w.put(path, 'value', str(p.value));
        w.put(path, 'note', str(p.note ?? ''));
        w.put(path, 'status', str('open'));
        w.put(path, 'createdAt', timestamp(createdAt));
      });
    });
    return ids;
  }

  /** `addAgentComments`: each comment not already in its fragment's margin, as `author` (an agent), one change per
   *  fragment committed with the author's name. Returns how many were added. */
  addAgentComments(comments: AgentComment[], author: string): number {
    let added = 0;
    const byFragment = new Map<string, AgentComment[]>();
    for (const c of comments) byFragment.set(c.fragmentID, [...(byFragment.get(c.fragmentID) ?? []), c]);
    for (const [fragmentID, group] of byFragment) {
      const doc = this.docs.get(fragmentDocName(fragmentID));
      if (!doc) continue;
      const r = new SwiftReader(doc);
      const text = r.string(ROOT, 'text') ?? '';
      const count = scalarLength(text);
      const all = r.map(ROOT, 'comments');
      const existing = new Set(all ? r.keys(all) : []);
      const heads = encodeHeads(A.getHeads(doc));
      const fresh = group.filter((c) => !existing.has(c.id));
      if (!fresh.length) continue;
      const createdAt = this.now();
      this.change(fragmentDocName(fragmentID), author, createdAt, (w) => {
        for (const c of fresh) {
          const lower = Math.min(Math.max(0, c.range[0]), count);
          const upper = Math.min(Math.max(lower, c.range[1]), count);
          writeComment(w, 'comments', { id: c.id, author, authorKind: 'agent', body: c.body, anchorStart: lower, anchorEnd: upper, anchorHeads: heads,
            quote: sliceScalars(text, lower, upper), parentID: null, status: 'open', createdAt, category: c.category });
        }
      });
      added += fresh.length;
    }
    return added;
  }

  /** `DocumentStore.createCheckpoint` with a creator other than the context's author. */
  createCheckpointBy(name: string, createdBy: string, trigger = 'manual'): { id: string; fragments: number; changed: string[] } {
    const author = this.context.author;
    (this.context as { author: string }).author = createdBy;
    try {
      const made = this.createCheckpoint(name, trigger);
      const record = readProject(this.doc('project')).checkpoints.find((c) => c.id === made.id);
      return { ...made, fragments: Object.keys(record?.fragmentHeads ?? {}).length };
    } finally {
      (this.context as { author: string }).author = author;
    }
  }
}

// MARK: Writes shared by the methods (and by the rooms' editors, inside their own changes)

/** `FragmentDocument.create`'s keys, in its order and types (inside the document's first change). */
export function writeFragmentKeys(w: Writer, d: Record<string, any>, o: { id: string; projectID: string; kind: FragmentKind; title: string; createdAt: Date }) {
  w.put([], 'schemaVersion', int(1));
  w.put([], 'type', str('fragment'));
  w.put([], 'id', str(o.id));
  w.put([], 'projectId', str(o.projectID));
  w.put([], 'epoch', int(0));
  w.put([], 'kind', str(o.kind));
  w.put([], 'title', str(o.title));
  d.synopsis = '';
  d.text = '';
  d.notes = '';
  d.metadata = {};
  w.put([], 'includeInCompile', bool(true));
  w.put([], 'createdAt', timestamp(o.createdAt));
  w.put([], 'modifiedAt', timestamp(o.createdAt));
  d.comments = {};
  d.suggestions = {};
  d.attachments = [];
  if (o.kind === 'map') writeMapKeys(w);
}

/** `setText`: the key's text object (made if missing), updated by a minimal diff so anchors hold. */
function setTextIn(d: Record<string, any>, key: string, text: string) {
  const current = d[key];
  if (typeof current !== 'string') d[key] = '';
  if (d[key] === text) return;
  A.updateText(d as A.Doc<unknown>, [key], text);
}

/** `registerFragment`: the fragment's entry in the project (kind, trashed; `createdAt` only if it has none; no `source`). */
export function registerFragment(w: Writer, e: { id: string; kind: FragmentKind; trashed: boolean; createdAt: Date }) {
  const path = ['fragments', e.id];
  w.putIfChanged(path, 'kind', str(e.kind));
  w.putIfChanged(path, 'trashed', bool(e.trashed));
  const obj = w.r.map(ROOT, 'fragments');
  const entry = obj ? w.r.map(obj, e.id) : undefined;
  if (!entry || w.r.date(entry, 'createdAt') === undefined) w.put(path, 'createdAt', timestamp(e.createdAt));
  w.delete(path, 'source');
}

// MARK: Field options in place (Fields.swift)

type FieldOption = MetadataField['options'][number];

function fieldNameOf(name: string): string {
  const trimmed = normalizedFieldName(name);
  if (trimmed === '') throw new ModelError('invalid', 'A field needs a name.');
  if (graphemes(trimmed).length > FIELD_NAME_LIMIT) throw new ModelError('invalid', `A field's name can be up to ${FIELD_NAME_LIMIT} characters.`);
  return trimmed;
}

function optionNameOf(name: string): string {
  const trimmed = normalizedFieldName(name);
  if (trimmed === '') throw new ModelError('invalid', 'An option needs a name.');
  if (graphemes(trimmed).length > FIELD_NAME_LIMIT) throw new ModelError('invalid', `An option's name can be up to ${FIELD_NAME_LIMIT} characters.`);
  return trimmed;
}

function requireOption(field: MetadataField, id: string): FieldOption {
  const option = field.options.find((o) => o.id === id);
  if (!option) throw new ModelError('notFound', `option ${id} in ${field.name}`);
  return option;
}

/** The field's options list in a change, made if it has none. */
function optionList(d: Record<string, any>, fieldID: string): any[] & { insertAt(i: number, v: unknown): void; deleteAt(i: number): void } {
  const field = d.metadataSchema[fieldID];
  if (!Array.isArray(field.options)) field.options = [];
  return field.options;
}

const elementID = (el: any): string | undefined => (el !== null && typeof el === 'object' && el.id !== undefined && el.id !== null ? String(el.id) : undefined);

/** The list elements holding `id` (more than one after two devices moved it). */
const optionElements = (list: any[], id: string) => list.filter((el) => elementID(el) === id);

function deleteOption(list: ReturnType<typeof optionList>, id: string) {
  for (let i = list.length - 1; i >= 0; i--) if (elementID(list[i]) === id) list.deleteAt(i);
}

/** Where in the stored list the option shown at `shown` is (repeats skipped); the end for undefined or past the last. */
function rawIndex(list: any[], shown?: number): number {
  if (shown === undefined) return list.length;
  const seen = new Set<string>();
  let n = 0;
  for (let i = 0; i < list.length; i++) {
    const id = elementID(list[i]);
    if (id === undefined || seen.has(id)) continue;
    seen.add(id);
    if (n === shown) return i;
    n++;
  }
  return list.length;
}

function insertOptionAt(list: ReturnType<typeof optionList>, i: number, o: FieldOption) {
  list.insertAt(i, {});
  list[i].id = new A.ImmutableString(o.id);
  list[i].name = new A.ImmutableString(o.name);
  if (o.color !== undefined) list[i].color = new A.ImmutableString(o.color);
}

/** `ProjectDocument.createArrangement`: ordered after every other arrangement unless `order` is given. */
export function writeArrangement(w: Writer, project: ProjectRecord, id: string, name: string, role: ArrangementRole, order?: number) {
  const path = ['arrangements', id];
  w.putIfChanged(path, 'name', str(name));
  w.putIfChanged(path, 'role', str(role));
  w.putIfChanged(path, 'order', f64(order ?? Math.max(-1, ...project.arrangements.map((a) => a.order)) + 1));
  w.ensure([...path, 'nodes']);
}

/** `putField`: the options list is replaced (a new list object) only when it differs from the stored one. */
export function writeField(w: Writer, d: Record<string, any>, field: FieldSpec, project?: ProjectRecord) {
  const path = ['metadataSchema', field.id];
  w.putIfChanged(path, 'name', str(field.name));
  w.putIfChanged(path, 'type', str(field.type));
  w.putIfChanged(path, 'order', f64(field.order));
  const options = field.options ?? [];
  const existing = project?.fields.find((f) => f.id === field.id)?.options ?? [];
  const same = existing.length === options.length && existing.every((o, i) =>
    o.id === options[i].id && o.name === options[i].name && (o.color ?? null) === (options[i].color ?? null));
  if (same) return;
  d.metadataSchema[field.id].options = [];
  const list = d.metadataSchema[field.id].options;
  options.forEach((o, i) => {
    list.insertAt(i, {});
    list[i].id = new A.ImmutableString(o.id);
    list[i].name = new A.ImmutableString(o.name);
    if (o.color !== undefined) list[i].color = new A.ImmutableString(o.color);
  });
}

/** `putEntity`'s writes (SuiteModel/StoryModel.swift), for use inside any project change (several entities in one). */
export function writeEntity(w: Writer, e: StoryEntity, color: string | undefined) {
  const path = ['entities', e.id];
  const optional = (key: string, value: Scalar | undefined) => (value ? w.putIfChanged(path, key, value) : w.delete(path, key));
  w.putIfChanged(path, 'kind', str(e.kind as EntityKind));
  w.putIfChanged(path, 'name', str(e.name));
  w.putIfChanged(path, 'aliases', str(e.aliases.join('\n')));
  w.putIfChanged(path, 'notes', str(e.notes));
  optional('birth', e.birth ? timestamp(e.birth) : undefined);
  optional('latitude', e.latitude !== undefined ? f64(e.latitude) : undefined);
  optional('longitude', e.longitude !== undefined ? f64(e.longitude) : undefined);
  w.putOptional(path, 'timeZone', e.timeZoneID);
  w.putOptional(path, 'role', e.role !== undefined && e.role.trim() !== '' ? e.role : undefined);
  // Never cleared: an editor holding an older copy mustn't unlink the study page. Nor is the colour.
  if (e.studyID !== undefined) w.putIfChanged(path, 'study', str(e.studyID));
  if (color !== undefined) w.putIfChanged(path, 'color', str(color));
}

function writeComment(w: Writer, key: 'comments' | 'synopsisComments', c: {
  id: string; author: string; authorKind: 'human' | 'agent'; body: string; anchorStart: number; anchorEnd: number; anchorHeads: string;
  quote: string; parentID: string | null; status: 'open' | 'resolved'; createdAt: Date; category?: string | null;
}) {
  const path = [key, c.id];
  w.putIfChanged(path, 'author', str(c.author));
  w.putIfChanged(path, 'authorKind', str(c.authorKind));
  w.putIfChanged(path, 'body', str(c.body));
  w.putIfChanged(path, 'anchorStart', { t: 'uint', v: c.anchorStart });
  w.putIfChanged(path, 'anchorEnd', { t: 'uint', v: c.anchorEnd });
  w.putIfChanged(path, 'anchorHeads', str(c.anchorHeads));
  w.putIfChanged(path, 'quote', str(c.quote));
  w.putOptional(path, 'parent', c.parentID);
  w.putIfChanged(path, 'status', str(c.status));
  w.putIfChanged(path, 'createdAt', timestamp(c.createdAt));
  w.putOptional(path, 'image', null);
  // Never cleared here, as the Swift's `putComment`: only `setCommentCategory` takes a comment out of its category.
  if (c.category) w.putIfChanged(path, 'category', str(c.category));
}

